/**
 * Perspective overlay geometry (FND-16, fit half). The fit's `evidence.frontQuad` gives the four
 * corners of the piece's front face on the photo (normalised image coordinates, TL, TR, BR, BL).
 * Overlay geometry is expressed in front-face coordinates (u, v): u = x / W from the left,
 * v = 1 − y / H from the top (so v grows downwards like the image). The homography of the quad
 * maps any (u, v) onto the photo; the UI draws the mapped segments.
 *
 * Without a quad, `carcassQuad(evidence.carcass)` gives the old frontal rectangle, so one code path
 * serves both.
 */
import type { BuildModel } from '../domain/schemas';
import type { FitResult } from './fit-schema';

export interface Pt { x: number; y: number }
/** 3 × 3 matrix, row-major, mapping (u, v, 1) to homogeneous image coordinates. */
export type Homography = readonly [number, number, number, number, number, number, number, number, number];

/** The quad of a frontal carcass rectangle (TL, TR, BR, BL). */
export const carcassQuad = (c: { left: number; right: number; top: number; bottom: number }): Pt[] => [
  { x: c.left, y: c.top }, { x: c.right, y: c.top }, { x: c.right, y: c.bottom }, { x: c.left, y: c.bottom },
];

/** The front quad of a fit: `frontQuad` when measured, else the carcass rectangle. */
export function frontQuadOf(fit: Pick<FitResult, 'evidence'>): Pt[] {
  const q = fit.evidence.frontQuad;
  return q && q.length === 4 ? q.map((p) => ({ x: p.x, y: p.y })) : carcassQuad(fit.evidence.carcass);
}

/**
 * The homography taking the unit square (0,0)→TL, (1,0)→TR, (1,1)→BR, (0,1)→BL to the quad
 * (Heckbert's square-to-quad). Exact for any convex quad; an affine map when the quad is a parallelogram.
 */
export function quadHomography(q: readonly Pt[]): Homography {
  const [p0, p1, p2, p3] = q as [Pt, Pt, Pt, Pt];
  const sx = p0.x - p1.x + p2.x - p3.x;
  const sy = p0.y - p1.y + p2.y - p3.y;
  if (Math.abs(sx) < 1e-12 && Math.abs(sy) < 1e-12) {
    return [p1.x - p0.x, p3.x - p0.x, p0.x, p1.y - p0.y, p3.y - p0.y, p0.y, 0, 0, 1];
  }
  const dx1 = p1.x - p2.x, dx2 = p3.x - p2.x, dy1 = p1.y - p2.y, dy2 = p3.y - p2.y;
  const den = dx1 * dy2 - dx2 * dy1;
  const g = (sx * dy2 - dx2 * sy) / den;
  const h = (dx1 * sy - sx * dy1) / den;
  return [p1.x - p0.x + g * p1.x, p3.x - p0.x + h * p3.x, p0.x, p1.y - p0.y + g * p1.y, p3.y - p0.y + h * p3.y, p0.y, g, h, 1];
}

/** Map a front-face point (u, v) to normalised image coordinates. */
export function mapUV(m: Homography, u: number, v: number): Pt {
  const w = m[6] * u + m[7] * v + m[8];
  return { x: (m[0] * u + m[1] * v + m[2]) / w, y: (m[3] * u + m[4] * v + m[5]) / w };
}

export interface FaceSegment { partId: string; role: string; u0: number; v0: number; u1: number; v1: number }

/**
 * The front elevation of a build as segments in front-face (u, v) coordinates: each part's
 * front outline (backs and hidden rails excluded). Map each end through `mapUV` to draw it on
 * the photo.
 */
export function frontFaceSegments(b: Pick<BuildModel, 'parts' | 'envelope'>): FaceSegment[] {
  const { W, H } = b.envelope;
  const out: FaceSegment[] = [];
  for (const p of b.parts) {
    if (p.role === 'back') continue;
    const { x, y, sx, sy } = p.placement;
    const u0 = x / W, u1 = (x + sx) / W, vTop = 1 - (y + sy) / H, vBot = 1 - y / H;
    for (const [a, c, d, e] of [[u0, vTop, u1, vTop], [u1, vTop, u1, vBot], [u1, vBot, u0, vBot], [u0, vBot, u0, vTop]] as const) {
      out.push({ partId: p.id, role: p.role, u0: a, v0: c, u1: d, v1: e });
    }
  }
  return out;
}
